import React, { useEffect, useState } from 'react'
import { BottomAdBox, FullHeightDiv, ListingsHeader } from './components/ListingsPage/ListingsPage.styles'
import { getTheme } from '../common/getTheme'
import { Flex, ThemeProvider } from 'pcln-design-system'
import {
  Footer as PartnerFooter,
  Header as PartnerHeader,
  PartnerProvider,
} from '@pcln/whitelabel-components'
import { FeatureProvider } from '@pcln/feature-components'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AdContainer } from './containers/ad-container'
import MediaQueryMatch from '@pcln/media-query-match'
import { ResponsiveChangeSearchHeaderListingsWrapper } from './components/ResponsiveChangeSearchHeaderWrapper/ResponsiveChangeSearchHeaderWrapper'
import { translateSearchRequest } from './util/translateSearchRequest'
import { ListingsPageV2 } from './components/ListingsPage/ListingsPageV2'
import { StickyCSHWrapper } from './components/StickyCSH/StickyCSHWrapper'
import { useSelector } from 'react-redux'
import { useIsMobile } from './helpers/useIsMobile'
import { StickyCSHListener } from './components/StickyCSH/StickyCSHListener'
import ClientOnly from '../common-components/ClientOnly'

import { useHistory } from 'react-router-dom'
import { navigateToFiltersPage } from './util/routingHelper'

import { APOLLO_CLIENT_NAME } from '../common/constants/constants'
import { sendUrlToIframeParentWindow, redirectToAgentPlatform } from '@pcln/travel-agent-utils'
import { useDepartureSummary } from './components/Listings/hooks/useDepartureSummary'
import { PackageListingsSearchHeader } from './components/PackageListingsSearchHeader'
import { isServerSide } from './util/isServerSide'
import { ToastProvider } from '@pcln/horizon'
import pclnExpt from '@pcln/experimentation'

const stickyCSHAnchorId = 'sticky-csh-anchor'
const stickyCSHWrapperId = 'sticky-csh-wrapper'
const hiddenClassName = 'sticky-dynamic-hide'

const ListingsClientV2 = ({ pclnBootstrapData, appName, variantLoader }) => {
  const shouldAllowInternationalLapInfants = pclnExpt.isVariant('PKG_ENABLE_FLY_LAP_INFANT_TICKETING')
  const isListingsLoading = useSelector((state) => state.listingsIsLoading)

  const [selectedDepartureSummaries, setSelectedDepartureSummaries] = useState([])
  const { handleTabChange } = useDepartureSummary({
    selectedDepartureSummaries,
    setSelectedDepartureSummaries,
  })

  const isMobileFromServer = useSelector((state) => state?.pclnBootstrapData?.isMobile)
  const listingsHasErrored = useSelector((state) => state?.listingsHasErrored)
  const isMobileFromClient = useIsMobile()
  const isMobile = isServerSide ? isMobileFromServer : isMobileFromClient

  const history = useHistory()

  const {
    partner,
    features,
    featuresContext,
    BOOTSTRAP_ENV,
    appVersion,
    isReshopExperience,
    offerToken,
    searchRequest,
    isPackageListings,
    bundleComponents,
  } = pclnBootstrapData ?? {}
  const { slices, tripType, cabinClass, travelers } = translateSearchRequest(searchRequest)
  const theme = getTheme()

  const activeTabId = useSelector((state) => state?.activeTabId)

  const handleFiltersClick = () => navigateToFiltersPage(history)

  const [forceOpen, setForceOpen] = useState(false)

  const forceHeaderOpen = () => {
    setForceOpen((prevForceOpen) => !prevForceOpen)
  }

  useEffect(() => {
    // For agent platform: send the url of the page in the iframe to the parent window
    if (features?.whiteLabel?.iframeEnabled) {
      sendUrlToIframeParentWindow()
    }
    // For agent platform: redirect to agent platform if not iframed
    if (features?.whiteLabel?.phoneAgentRedirectToAgentPlatformEnabled) {
      redirectToAgentPlatform()
    }
  }, [features?.whiteLabel?.iframeEnabled, features?.whiteLabel?.phoneAgentRedirectToAgentPlatformEnabled])

  return (
    <ThemeProvider theme={theme}>
      <FeatureProvider features={features} context={featuresContext}>
        <PartnerProvider appName={appName} partner={partner} env={BOOTSTRAP_ENV}>
          <ErrorBoundary appName={appName} appVersion={appVersion}>
            {partner && <PartnerHeader />}
            <FullHeightDiv>
              {isPackageListings ? (
                <PackageListingsSearchHeader
                  searchRequest={searchRequest}
                  bundleComponents={bundleComponents}
                  handleFiltersClick={handleFiltersClick}
                  shouldAllowInternationalLapInfants={shouldAllowInternationalLapInfants}
                />
              ) : (
                <StickyCSHWrapper
                  stickyCSHAnchorId={stickyCSHAnchorId}
                  stickyCSHWrapperId={stickyCSHWrapperId}
                  hiddenClassName={hiddenClassName}
                  showStickyHeader={!isListingsLoading}
                  isMobileFilterChip={isMobile}
                >
                  <ListingsHeader
                    bg='background.light'
                    minHeight={['68px', null, null, null, '112px']}
                    mb={[0, null, null, null, 3]}
                  >
                    <StickyCSHListener
                      stickyCSHAnchorId={stickyCSHAnchorId}
                      stickyCSHWrapperId={stickyCSHWrapperId}
                      hiddenClassName={hiddenClassName}
                    />
                    <ResponsiveChangeSearchHeaderListingsWrapper
                      forceOpen={forceOpen}
                      forceHeaderOpen={forceHeaderOpen}
                      defaultTripSlices={slices}
                      defaultTripType={tripType}
                      defaultCabinClass={cabinClass}
                      resetSearchFields
                      defaultPassengers={travelers}
                      defaultAirports={pclnBootstrapData.searchRequest?.airports}
                      filterOnClick={handleFiltersClick}
                      isWhiteLabel={!!partner}
                      urlRoot={'/m/fly/search/'}
                      isReshopExperience={isReshopExperience}
                      modifyNow={pclnBootstrapData?.searchRequest?.params?.userActionType}
                      pageName='listings'
                      queryParams={{
                        'offer-token': offerToken || undefined,
                      }}
                      isMobile={isMobile}
                      handleBackLink={activeTabId > 1 ? () => handleTabChange({ id: activeTabId - 1 }) : null}
                      hideFilter={listingsHasErrored}
                      apolloClientInfo={{
                        clientName: APOLLO_CLIENT_NAME,
                        clientVersion: pclnBootstrapData?.appVersion,
                      }}
                    />
                  </ListingsHeader>
                </StickyCSHWrapper>
              )}

              <ToastProvider>
                <ListingsPageV2
                  forceHeaderOpen={forceHeaderOpen}
                  pclnBootstrapData={pclnBootstrapData}
                  setSelectedDepartureSummaries={setSelectedDepartureSummaries}
                  selectedDepartureSummaries={selectedDepartureSummaries}
                  handleTabChange={handleTabChange}
                  theme={theme}
                  variantLoader={variantLoader}
                />
              </ToastProvider>

              {!isPackageListings && (
                <Flex justifyContent={'center'}>
                  <MediaQueryMatch minWidth={'md'}>
                    <BottomAdBox mb={4}>
                      <ClientOnly>
                        <AdContainer
                          adId='div-gpt-ad-1470951919306-0'
                          isSticky={false}
                          minScreenSize='desktop'
                        />
                      </ClientOnly>
                    </BottomAdBox>
                  </MediaQueryMatch>
                </Flex>
              )}
            </FullHeightDiv>
            {partner && <PartnerFooter />}
          </ErrorBoundary>
        </PartnerProvider>
      </FeatureProvider>
    </ThemeProvider>
  )
}

export default ListingsClientV2
